import { RoomType } from "@prisma/client";
import { useSearchParams, Link } from "react-router";
import { useEffect, useState, Fragment } from "react";
import Loader from "@app/icons/Loader";
import PostCard from "@app/components/PostCard";
import Star from "@app/icons/Star";
import { PostLoadingData } from "@api/src/types/post";
import { PostsGetResponse } from "@api/src/types/responses";
import FeedHeader from "@app/components/FeedHeader";
import FeedNewPost from "@app/routes/feed.new-post";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import EditGroupName from "@app/components/EditGroupName";
import ButtonLeaveGroup from "@app/components/ButtonLeaveGroup";
import useMedspotStore from "@app/zustand/store";
import useMinimumWidth from "@app/utils/useMinimumWidth";
import FeedFilter from "@app/components/FeedFilter";

function numberToArray(n: number) {
  const arr = new Array(n);
  for (let i = 0; i < n; i++) {
    arr[i] = i + 1;
  }
  return arr;
}

export default function Feed() {
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const [newPostModalOpen, setNewPostModalOpen] = useState(false);

  const [searchParams] = useSearchParams();

  const roomId = searchParams.get("qRoomId");
  const qFavs = searchParams.get("qFavs");
  const qRoomType = searchParams.get("qRoomType");
  const isSearch = searchParams.has("search");

  const reset = () => {
    setPage(0);
    setResetKey((k) => k + 1);
  };

  useEffect(() => {
    reset();
  }, [roomId, qFavs]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const triggerLoadOffset = 100;
    const scrolledToBottom =
      e.currentTarget?.scrollTop + e.currentTarget.clientHeight >=
      e.currentTarget.scrollHeight - triggerLoadOffset;
    if (scrolledToBottom && hasMore && !loading) {
      setPage((p) => p + 1);
    }
  };

  const showingLoader = hasMore && !loading;

  return (
    <main
      className="relative flex w-full flex-col overflow-y-auto pt-4 md:px-6"
      onScroll={handleScroll}
      suppressHydrationWarning
    >
      <FeedNewPost
        open={newPostModalOpen}
        setOpen={setNewPostModalOpen}
        onNewPost={() => {
          reset();
          setNewPostModalOpen(false);
        }}
      />
      <FeedHeader setNewPostModalOpen={setNewPostModalOpen} />
      {!isSearch && (
        <div
          className={[
            "flex w-full flex-row px-2",
            // qRoomType === RoomType.DIRECT ? "justify-start" : "justify-center",
            "justify-start",
          ].join(" ")}
        >
          <button
            type="button"
            onClick={() => {
              reset();
            }}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg py-1 text-sky-900 disabled:opacity-50"
          >
            <ArrowPathIcon className="size-4" />{" "}
            <span className="mt-1 text-xs">Rafraîchir</span>
          </button>
        </div>
      )}
      <FeedFilter />
      {qRoomType === RoomType.DIRECT && (
        <div className="flex w-full flex-col items-start justify-between px-2">
          <div className="flex items-center">
            <Link
              to={`/users?qRoomId=${roomId}&qRoomType=${RoomType.DIRECT}`}
              className="w-max cursor-pointer py-1 text-xs text-sky-900"
            >
              Gérer les participants
            </Link>
          </div>
          <div className="flex items-center">
            <ButtonLeaveGroup />
          </div>
          <div className="flex items-center">
            <EditGroupName
              key={roomId}
              className="w-max cursor-pointer py-1 text-xs text-sky-900"
            />
          </div>
        </div>
      )}
      <Fragment key={resetKey}>
        {numberToArray(page + 1).map((p, index) => {
          return (
            <Page
              showingLoader={showingLoader}
              key={p + resetKey}
              page={index}
              setLoading={setLoading}
              setHasMore={setHasMore}
            />
          );
        })}
      </Fragment>
      {showingLoader && (
        <div className="-mb-1 mt-1 flex items-center justify-center">
          <Loader />
        </div>
      )}
    </main>
  );
}

function Page({
  page,
  showingLoader,
  setLoading,
  setHasMore,
}: {
  page: number;
  showingLoader: boolean;
  setLoading: (loading: boolean) => void;
  setHasMore: (hasMore: boolean) => void;
}) {
  const [posts, setPosts] = useState<PostLoadingData["posts"]>([]);
  const [searchParams] = useSearchParams();
  const [showLoader, setShowLoader] = useState(false);
  const isDrawerOpen = useMedspotStore((state) => state.drawerOpen);
  const isDrawerIsOpening = useMedspotStore((state) => state.drawerIsOpening);
  const isDesktop = useMinimumWidth("md");

  const qRoomId = searchParams.get("qRoomId");
  const qFavs = searchParams.get("qFavs");
  const searchQuery = searchParams.get("search");

  useEffect(() => {
    const fetchPosts = async () => {
      if (!qRoomId && !qFavs && !searchQuery) return;
      if (!isDesktop && isDrawerOpen) return;

      setLoading(true);
      setShowLoader(true);
      const queryString = new URLSearchParams();
      queryString.set("page", String(page));
      if (searchParams.has("search")) {
        queryString.set("search", searchParams.get("search") ?? "");
      }
      if (qRoomId) {
        queryString.set("qRoomId", qRoomId);
      }
      if (qFavs) {
        queryString.set("qFavs", qFavs);
      }

      try {
        fetch(
          `${import.meta.env.VITE_API_URL}/post?${queryString.toString()}`,
          {
            credentials: "include",
          },
        )
          .then((res) => res.json() as Promise<PostsGetResponse>)
          .then((response) => {
            setPosts(response.data.posts);
            setHasMore(response.data.hasMore);
          })
          .finally(() => {
            setLoading(false);
            setShowLoader(false);
          });
      } catch (error) {
        console.error("Failed to fetch posts:", error);
        setLoading(false);
      }
    };

    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, qFavs, qRoomId, searchQuery, isDesktop, isDrawerOpen]);

  if (!isDesktop && isDrawerOpen && !isDrawerIsOpening) return null;

  return (
    <>
      {!showingLoader && showLoader && (
        <div className="-mb-1 mt-1 flex items-center justify-center">
          <Loader />
        </div>
      )}
      {posts.map((post) => {
        return (
          <PostCard
            key={post.id}
            post={post}
            showRoomName={qFavs === "true" || !!searchQuery}
          />
        );
      })}
      {!posts.length && !showLoader && (
        <p className="mx-auto mt-[20%] max-w-prose py-4 text-center text-sky-900">
          {searchParams.has("search") ? (
            <>
              Vous pouvez rechercher parmi tous vos canaux de discussion en
              tapant dans la barre de recherche ci-dessus.
              <br />
              <br />
              🔎
              <br />
              Vous pourrez ensuite toutes les voir ici
            </>
          ) : qFavs ? (
            <>
              Pour enregistrer des publications, cliquez sur le bouton en forme
              d'étoile, en haut à droite de chacune
              <br />
              <br />
              <Star
                filled={false}
                className="pointer-events-none mx-auto size-10"
              />
              .
              <br />
              Vous pourrez ensuite toutes les voir ici
            </>
          ) : (
            <>
              C'est le tout début de ce canal de discussion.
              <br />
              Vous avez l'honneur de pouvoir y écrire le premier message !
              <br />
              🧑‍⚕️ ✍️
            </>
          )}
        </p>
      )}
    </>
  );
}
